/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography'
import { colors } from '../../../shared/theme/colors';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { showError, showSuccess } from '../../../shared/ui/appToast';
import {
  fetchMyOrganizations,
  type UserOrganizationSummary,
} from '../organizationSwitcherService';
import { performOrganizationSwitch } from '../organizationSwitchFlow';
import { navigateRoot } from '../../navigation/rootNavigation';
import { OrganizationAccessGrantsSheet } from './OrganizationAccessGrantsSheet';
import { fetchBusinessTypes } from '../../../onboarding/application/businessTypeService';
import {
  resolveBusinessTypeDisplayLabel,
  type BusinessTypeListItem,
} from '../domain/businessProfile';
import { EXTERNAL_ORG_ACCESS_UI_ENABLED } from '../organizationExternalAccessFeature';

type Props = {
  visible: boolean;
  onClose: () => void;
  navigation: NavigationProp<ParamListBase>;
};

function orgRoleLabel(org: UserOrganizationSummary): string {
  if (org.is_owner) return 'Dueño';
  if (org.is_grant) {
    if (org.grant_role === 'contador') return 'Contador';
    if (org.grant_role === 'auditor') return 'Auditor';
    return 'Acceso externo';
  }
  return 'Empleado';
}

export function OrganizationSwitcherSheet({ visible, onClose, navigation }: Props) {
  const insets = useSafeAreaInsets();
  const { width, height: screenHeight } = useWindowDimensions();
  const isDesktop = width >= 768;
  const sheetMaxHeight = screenHeight * 0.88;
  const scrollMaxHeight = sheetMaxHeight - 120;
  const [loading, setLoading] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<UserOrganizationSummary[]>([]);
  const [businessTypes, setBusinessTypes] = useState<BusinessTypeListItem[]>([]);
  const [grantsOpen, setGrantsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [payload, types] = await Promise.all([
        fetchMyOrganizations({ force: true }),
        fetchBusinessTypes('AR'),
      ]);
      setOrganizations(payload?.organizations ?? []);
      setBusinessTypes(types);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (visible) void load();
  }, [visible, load]);

  const activeOwned = useMemo(
    () => organizations.find((o) => o.is_active && o.is_owner) ?? null,
    [organizations],
  );
  const canAddOrg = organizations.some((o) => o.is_owner);

  const rubroLabel = useCallback(
    (org: UserOrganizationSummary) =>
      resolveBusinessTypeDisplayLabel(org.business_type_code, null, businessTypes),
    [businessTypes],
  );

  const handleSelect = async (org: UserOrganizationSummary) => {
    if (org.is_active) {
      onClose();
      return;
    }
    setSwitchingId(org.id);
    try {
      const ok = await performOrganizationSwitch(org.id, navigation);
      if (!ok) {
        showError('No pudimos cambiar de negocio', 'Probá de nuevo en unos segundos.');
        return;
      }
      showSuccess('Negocio cambiado', `Ahora estás en ${org.name}.`);
      onClose();
    } finally {
      setSwitchingId(null);
    }
  };

  return (
    <Modal visible={visible} transparent animationType={isDesktop ? 'fade' : 'slide'} onRequestClose={onClose}>
      <View style={[styles.backdrop, isDesktop && { justifyContent: 'center', alignItems: 'center' }]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel="Cerrar" />
        <View
          style={[
            styles.sheet,
            { paddingBottom: Math.max(insets.bottom, spacing.lg), maxHeight: sheetMaxHeight },
            isDesktop && { maxWidth: 480, borderRadius: 16, width: '100%' },
          ]}
          testID="org-switcher.sheet"
        >
          <View style={styles.grab} />
          <View style={styles.header}>
            <Text style={styles.title}>Tus negocios</Text>
            <TouchableOpacity onPress={onClose} accessibilityRole="button">
              <Text style={styles.close}>Cerrar</Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.hint}>Cada negocio tiene su rubro, plan y sucursales.</Text>
          <ScrollView
            style={{ maxHeight: scrollMaxHeight }}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
          >
            {loading ? <ActivityIndicator color={ticketColors.gold} style={styles.loader} /> : null}
            {organizations.map((org) => (
              <TouchableOpacity
                key={org.id}
                style={[styles.option, org.is_active && styles.optionActive]}
                onPress={() => void handleSelect(org)}
                disabled={switchingId != null}
                accessibilityRole="button"
                testID={`org-switcher.option.${org.id}`}
              >
                <View style={styles.optionText}>
                  <Text style={styles.optionName}>{org.name}</Text>
                  <Text style={styles.optionMeta}>
                    {rubroLabel(org)} · {orgRoleLabel(org)}
                  </Text>
                </View>
                {org.is_active ? <Text style={styles.now}>Activo</Text> : null}
                {switchingId === org.id ? (
                  <ActivityIndicator color={ticketColors.gold} />
                ) : (
                  <Ionicons name="chevron-forward" size={18} color={ticketColors.ink3} />
                )}
              </TouchableOpacity>
            ))}
            {EXTERNAL_ORG_ACCESS_UI_ENABLED && activeOwned ? (
              <TouchableOpacity
                style={styles.grantsRow}
                onPress={() => setGrantsOpen(true)}
                accessibilityRole="button"
                testID="org-switcher.grants"
              >
                <Ionicons name="people-outline" size={20} color={colors.accent} style={styles.grantsIcon} />
                <View style={styles.grantsTextWrap}>
                  <Text style={styles.grantsTitle}>Contador o auditor</Text>
                  <Text style={styles.grantsSubtitle}>Dar acceso de solo lectura a tu negocio</Text>
                </View>
                <Ionicons name="chevron-forward" size={18} color={ticketColors.ink3} />
              </TouchableOpacity>
            ) : null}
          </ScrollView>
          {canAddOrg ? (
            <TouchableOpacity
              style={styles.addOrg}
              onPress={() => {
                onClose();
                navigateRoot(navigation, 'Onboarding', { mode: 'additional_organization' });
              }}
              accessibilityRole="button"
              testID="org-switcher.add"
            >
              <Text style={styles.addOrgText}>+ Agregar otro negocio</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
      {EXTERNAL_ORG_ACCESS_UI_ENABLED && activeOwned ? (
        <OrganizationAccessGrantsSheet
          visible={grantsOpen}
          onClose={() => setGrantsOpen(false)}
          organizationId={activeOwned.id}
          organizationName={activeOwned.name}
        />
      ) : null}
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28, 25, 23, 0.35)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: spacing.lg,
    overflow: 'hidden',
  },
  scrollContent: {
    paddingBottom: spacing.xs,
  },
  grab: {
    width: 40,
    height: 4,
    borderRadius: 999,
    backgroundColor: 'rgba(28, 25, 23, 0.15)',
    alignSelf: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  title: {
    ...typography.title3,
    color: colors.textPrimary,
  },
  close: {
    ...typography.body,
    fontWeight: '700',
    color: colors.accent,
  },
  hint: {
    ...typography.caption,
    color: colors.textSecondary,
    marginBottom: spacing.md,
    lineHeight: 18,
  },
  loader: { marginVertical: spacing.lg },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 14,
    padding: spacing.md,
    marginBottom: spacing.sm,
    backgroundColor: ticketColors.paper,
  },
  optionActive: {
    borderColor: colors.accent,
    backgroundColor: colors.accentSoft,
  },
  optionText: { flex: 1, minWidth: 0 },
  optionName: {
    ...typography.body,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  optionMeta: {
    ...typography.caption,
    color: colors.textSecondary,
    marginTop: 2,
  },
  now: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.accent,
    textTransform: 'uppercase',
  },
  grantsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 14,
    padding: spacing.md,
    marginTop: spacing.xs,
    backgroundColor: ticketColors.paper,
  },
  grantsIcon: {
    flexShrink: 0,
  },
  grantsTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  grantsTitle: {
    ...typography.body,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  grantsSubtitle: {
    ...typography.caption,
    color: colors.textSecondary,
    marginTop: 2,
    lineHeight: 18,
  },
  addOrg: {
    marginTop: spacing.sm,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: ticketColors.gold,
    borderRadius: 14,
    padding: spacing.md,
    alignItems: 'center',
  },
  addOrgText: {
    ...typography.body,
    fontWeight: '600',
    color: colors.accent,
  },
});
